// src/screens/pass/PassExtraDetailsView.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Space,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

import { CONFIG } from "../../atoms/config";
import { checkRegoNumber, checkUserPassesForRego } from "../../atoms/passNetwork";

const DEBOUNCE_MS = 2000;

function normalizeRego(s) {
  return String(s || "").trim().toUpperCase();
}
function safeStr(v) {
  return String(v ?? "").trim();
}
function isRegoUsable(rego) {
  return normalizeRego(rego).length >= 3;
}

function parseExistingPasses(data, fallbackRego) {
  const passesArray = Array.isArray(data?.passes)
    ? data.passes
    : Array.isArray(data?.passes?.data)
    ? data.passes.data
    : [];

  return passesArray
    .map((p) => {
      const id = Number(p?.id || 0);
      const costCents = Number(p?.cost || 0);
      const duration = Number(p?.duration || 0);

      const startDateStr = p?.start_date?.date;
      const endDateStr = p?.end_date?.date;

      const userName = p?.user?.name || "";
      const userPhone = p?.user?.phone || "";

      return {
        id,
        carRegistration: p?.car_registration || fallbackRego || "",
        code: p?.code || "",
        duration,
        costCents,
        startUTC: startDateStr
          ? new Date(startDateStr.replace(" ", "T") + "Z")
          : null,
        endUTC: endDateStr ? new Date(endDateStr.replace(" ", "T") + "Z") : null,
        carAddress: p?.car_address || "",
        userName,
        userPhone,
      };
    })
    .filter((p) => p.id > 0 || p.code || p.carRegistration);
}

function prettyDate(d) {
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  } catch {
    return "—";
  }
}

export default function PassExtraDetailsView({
  HeaderLogo,
  siteId,
  phoneE164,
  registrationNumber,
  address,
  onChangeRegistrationNumber,
  onChangeAddress,
  onBack,
  onContinue,
  onSelectExistingPass,
}) {
  // ─────────────────────────────────────────────
  // Existing-pass check state (Swift-like)
  // ─────────────────────────────────────────────
  const [isCheckingRego, setIsCheckingRego] = useState(false);
  const [regoCheckMessage, setRegoCheckMessage] = useState("");
  const [regoHasExistingPass, setRegoHasExistingPass] = useState(null); // null/true/false
  const [existingPasses, setExistingPasses] = useState([]);

  const lastCheckedRegoRef = useRef("");
  const debounceRef = useRef(null);
  const inFlightAbortRef = useRef(null);
  const requestSeqRef = useRef(0);

  // ─────────────────────────────────────────────
  // State selector + manual rego validation (Swift-like)
  // ─────────────────────────────────────────────
  const availableStates = useMemo(
    () => ["TAS", "NSW", "VIC", "QLD", "WA", "SA", "ACT", "NT"],
    []
  );
  const [selectedState, setSelectedState] = useState("TAS");

  const [isValidatingRego, setIsValidatingRego] = useState(false);
  const [regoIsValid, setRegoIsValid] = useState(null); // null/true/false
  const [regoValidationMessage, setRegoValidationMessage] = useState("");
  const [validatedVehicleSummary, setValidatedVehicleSummary] = useState(null);
  const lastValidatedKeyRef = useRef("");

  // ─────────────────────────────────────────────
  // Google Places (keep your web autocomplete)
  // ─────────────────────────────────────────────
  const [placesReady, setPlacesReady] = useState(false);
  const [placesErr, setPlacesErr] = useState("");
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Derived
  const normalizedRego = useMemo(
    () => normalizeRego(registrationNumber),
    [registrationNumber]
  );
  const regoValid = useMemo(() => isRegoUsable(normalizedRego), [normalizedRego]);

  const hasExistingPass = existingPasses.length > 0;

  // Mirrors Swift:
  const shouldShowStateSelection =
    regoHasExistingPass === false && !isCheckingRego && !hasExistingPass;

  const shouldShowAddressField =
    regoHasExistingPass === false && !isCheckingRego && !hasExistingPass;

  const canValidateRego = useMemo(() => {
    if (hasExistingPass) return false;
    if (regoHasExistingPass !== false) return false;
    if (isCheckingRego || isValidatingRego) return false;

    const regoOk = normalizeRego(registrationNumber).length >= 3;
    const addressOk = safeStr(address).length > 0;
    return regoOk && addressOk;
  }, [
    hasExistingPass,
    regoHasExistingPass,
    isCheckingRego,
    isValidatingRego,
    registrationNumber,
    address,
  ]);

  const canContinue = useMemo(() => {
    if (hasExistingPass) return true;
    if (regoHasExistingPass !== false) return false;

    const regoOk = normalizeRego(registrationNumber).length >= 3;
    const addressOk = safeStr(address).length > 0;

    return regoOk && addressOk && regoIsValid === true;
  }, [hasExistingPass, regoHasExistingPass, registrationNumber, address, regoIsValid]);

  const bottomButtonTitle = useMemo(() => {
    if (hasExistingPass) return "View Pass";
    if (regoIsValid === true) return "Continue to Payment";
    return "Validate registration number";
  }, [hasExistingPass, regoIsValid]);

  const bottomButtonDisabled = useMemo(() => {
    if (hasExistingPass) return isCheckingRego || isValidatingRego;
    if (regoIsValid === true) return !canContinue || isCheckingRego || isValidatingRego;
    return !canValidateRego;
  }, [hasExistingPass, regoIsValid, canContinue, isCheckingRego, isValidatingRego, canValidateRego]);

  function resetValidation() {
    setIsValidatingRego(false);
    setRegoIsValid(null);
    setRegoValidationMessage("");
    setValidatedVehicleSummary(null);
    lastValidatedKeyRef.current = "";
  }

  // ─────────────────────────────────────────────
  // Places: load + attach only when address field is visible
  // ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function loadPlaces() {
      if (!shouldShowAddressField) return;

      const key = CONFIG.GoogleMapAPI;
      if (!key) {
        setPlacesErr("Missing Google Maps API key (CONFIG.GoogleMapAPI).");
        setPlacesReady(false);
        return;
      }

      try {
        setPlacesErr("");
        setOptions({ key, libraries: ["places"] });
        await importLibrary("places");
        if (cancelled) return;
        setPlacesReady(true);
      } catch (e) {
        if (cancelled) return;
        setPlacesErr(e?.message || "Failed to load Google Places.");
        setPlacesReady(false);
      }
    }

    loadPlaces();
    return () => {
      cancelled = true;
    };
  }, [shouldShowAddressField]);

  useEffect(() => {
    if (!shouldShowAddressField) return;
    if (!placesReady) return;
    if (!addressInputRef.current) return;
    if (autocompleteRef.current) return;

    try {
      const google = window.google;
      if (!google?.maps?.places?.Autocomplete) {
        setPlacesErr("Google Places library not available.");
        return;
      }

      const ac = new google.maps.places.Autocomplete(addressInputRef.current, {
        types: ["address"],
        componentRestrictions: { country: ["au"] },
        fields: ["formatted_address", "name", "place_id"],
      });

      ac.addListener("place_changed", () => {
        const place = ac.getPlace?.();
        const formatted =
          place?.formatted_address ||
          place?.name ||
          addressInputRef.current?.value ||
          "";

        if (formatted) {
          onChangeAddress(formatted);
          resetValidation();
        }
      });

      autocompleteRef.current = ac;
    } catch (e) {
      setPlacesErr(e?.message || "Failed to initialise Google Autocomplete.");
    }

    return () => {
      autocompleteRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowAddressField, placesReady]);

  // ─────────────────────────────────────────────
  // Rego check: debounce + cancel + stale-guard (Swift-like)
  // ─────────────────────────────────────────────
  useEffect(() => {
    scheduleRegoCheck(normalizedRego);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedRego]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (inFlightAbortRef.current) {
        try {
          inFlightAbortRef.current.abort();
        } catch {}
      }
    };
  }, []);

  function scheduleRegoCheck(raw) {
    const rego = normalizeRego(raw);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (inFlightAbortRef.current) {
      try {
        inFlightAbortRef.current.abort();
      } catch {}
      inFlightAbortRef.current = null;
    }

    if (rego.length < 3) {
      setIsCheckingRego(false);
      setRegoCheckMessage("");
      setRegoHasExistingPass(null);
      setExistingPasses([]);
      lastCheckedRegoRef.current = "";
      resetValidation();
      return;
    }

    if (rego === lastCheckedRegoRef.current) return;

    // optimistic UI
    setIsCheckingRego(true);
    setRegoCheckMessage("");
    setRegoHasExistingPass(null);
    setExistingPasses([]);
    resetValidation();

    debounceRef.current = setTimeout(() => {
      checkExistingPassesForRego(rego);
    }, DEBOUNCE_MS);
  }

  async function checkExistingPassesForRego(rego) {
    lastCheckedRegoRef.current = rego;

    const reqId = ++requestSeqRef.current;
    const ac = new AbortController();
    inFlightAbortRef.current = ac;

    setIsCheckingRego(true);
    setRegoCheckMessage("");
    setRegoHasExistingPass(null);
    setExistingPasses([]);
    resetValidation();

    try {
      const res = await checkUserPassesForRego({
        siteId,
        phoneE164,
        rego,
        // best-effort: if your passNetwork forwards signal, this cancels
        signal: ac.signal,
      });

      if (reqId !== requestSeqRef.current) return;

      const ok =
        res?.status === true ||
        res?.status === 1 ||
        res?.data?.status === 1 ||
        res?.data?.status === true;

      const data = res?.data || res || {};

      if (!ok) {
        setRegoHasExistingPass(null);
        setRegoCheckMessage(data?.message || "Couldn’t check passes right now.");
        setExistingPasses([]);
        return;
      }

      const mapped = parseExistingPasses(data, rego);
      setExistingPasses(mapped);

      const hasFromList = mapped.length > 0;
      const hasFromFlags = Number(data?.count || 0) > 0 || data?.has_pass === true;

      if (hasFromList || hasFromFlags) {
        setRegoHasExistingPass(true);
        setRegoCheckMessage("");
        resetValidation();
        return;
      }

      setRegoHasExistingPass(false);
      setRegoCheckMessage("No existing passes found. Please purchase your pass.");
    } catch (e) {
      if (e?.name === "AbortError") return;
      if (reqId !== requestSeqRef.current) return;

      setRegoHasExistingPass(null);
      setRegoCheckMessage(e?.message || "Couldn’t check passes right now.");
      setExistingPasses([]);
    } finally {
      if (reqId === requestSeqRef.current) {
        setIsCheckingRego(false);
      }
    }
  }

  // ─────────────────────────────────────────────
  // Manual rego validation (Swift-like)
  // ─────────────────────────────────────────────
  async function validateRegoPressed() {
    if (!canValidateRego) return;

    const rego = normalizeRego(registrationNumber);
    const state = selectedState;
    const key = `${state}|${rego}`;

    if (key === lastValidatedKeyRef.current && regoIsValid === true) return;

    setIsValidatingRego(true);
    setRegoIsValid(null);
    setRegoValidationMessage("");
    setValidatedVehicleSummary(null);

    // cancel any previous validation request
    const ac = new AbortController();

    try {
      const result = await checkRegoNumber({ rego, state });

      console.log(result, 'wehjqhwejhwqejkhqwejkhqwejkhqwejkhqwejkh')

      if (!result?.status) {
        setRegoIsValid(false);
        setRegoValidationMessage(result?.message || "Couldn’t validate the registration number.");
        setValidatedVehicleSummary(null);
        lastValidatedKeyRef.current = key;
        return;
      }

      if (result.data.valid !== true) {
        setRegoIsValid(false);
        setRegoValidationMessage("That registration number isn’t valid.");
        setValidatedVehicleSummary(null);
        lastValidatedKeyRef.current = key;
        return;
      }

      setRegoIsValid(true);
      setRegoValidationMessage("");
      

      const vehicle = result.data.vehicle;
      setValidatedVehicleSummary(vehicle.year + ' ' + vehicle.color + ' ' + vehicle.make + ' ' + vehicle.model);

      lastValidatedKeyRef.current = key;
    } catch (e) {
      if (e?.name === "AbortError") return;
      setRegoIsValid(false);
      setRegoValidationMessage(
        e?.message || "Couldn’t validate the registration number right now."
      );
      setValidatedVehicleSummary(null);
      lastValidatedKeyRef.current = key;
    } finally {
      setIsValidatingRego(false);
    }
  }

  function onPressBottom() {
    if (isCheckingRego || isValidatingRego) return;

    if (hasExistingPass && existingPasses[0]) {
      onSelectExistingPass(existingPasses[0]);
      return;
    }

    if (regoIsValid === true) {
      onContinue(normalizeRego(registrationNumber), safeStr(address));
      return;
    }

    validateRegoPressed();
  }

  return (
    <Container fluid px={0} style={{ minHeight: "100vh" }}>
      <Container size={700} px="md" style={{ minHeight: "100vh", paddingBottom: 120 }}>
        <Space h={24} />

        <Title order={2}>Additional information</Title>
        <Text c="dimmed" mt={6}>
          We just need a few details for your camping registration.
        </Text>

        <Space h="xl" />

        <Paper withBorder radius="lg" p="md">
          {shouldShowStateSelection ? (
            <>
              <Text fw={600} size="sm">
                State or territory
              </Text>

              <SegmentedControl
                mt={6}
                fullWidth
                value={selectedState}
                onChange={(v) => {
                  setSelectedState(v);
                  resetValidation();
                }}
                data={availableStates.map((s) => ({ label: s, value: s }))}
              />

              <Divider my="md" />
            </>
          ) : null}

          <Text fw={600} size="sm">
            Vehicle registration number
          </Text>

          <TextInput
            mt={6}
            placeholder="e.g. ABC-123"
            value={registrationNumber}
            onChange={(e) => {
              onChangeRegistrationNumber(normalizeRego(e.currentTarget.value));
              resetValidation();
            }}
          />

          <Space h="sm" />

          {isCheckingRego ? (
            <Text size="sm" c="dimmed">
              Checking for existing passes…
            </Text>
          ) : hasExistingPass ? (
            <Box>
              <Text fw={700} size="sm" c="green">
                Existing pass found
              </Text>

              <Space h="sm" />

              {existingPasses.slice(0, 2).map((p) => (
                <Button
                  key={String(p.id) + p.code}
                  variant="light"
                  fullWidth
                  styles={{ inner: { justifyContent: "flex-start" } }}
                  onClick={() => onSelectExistingPass(p)}
                  mb="sm"
                  h="auto"
                >
                  <div style={{ textAlign: "left" }}>
                    <Text fw={700}>{p.carRegistration}</Text>
                    <Text size="sm" c="dimmed">
                      Pass Code: {p.code || "—"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Start: {prettyDate(p.startUTC)} • End: {prettyDate(p.endUTC)}
                    </Text>
                    {p.carAddress ? (
                      <Text fw={700} size="xs" c="dimmed">
                        {p.carAddress}
                      </Text>
                    ) : null}
                  </div>
                </Button>
              ))}

              {existingPasses.length > 2 ? (
                <Text size="xs" c="dimmed">
                  And {existingPasses.length - 2} more…
                </Text>
              ) : null}
            </Box>
          ) : regoCheckMessage ? (
            <Text size="sm" c="dimmed">
              {regoCheckMessage}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              Enter your rego to check for an existing pass.
            </Text>
          )}

          {regoHasExistingPass === false && !hasExistingPass ? (
            <>
              <Space h="sm" />

              {isValidatingRego ? (
                <Text size="sm" c="dimmed">
                  Validating registration number…
                </Text>
              ) : regoIsValid === true ? (
                <Box>
                  <Text fw={700} size="sm" c="green">
                    Registration number validated.
                  </Text>
                  {validatedVehicleSummary ? (
                    <Text size="sm" c="dimmed" mt={4}>
                      {validatedVehicleSummary}
                    </Text>
                  ) : null}
                </Box>
              ) : regoValidationMessage ? (
                <Text size="sm" c="red">
                  {regoValidationMessage}
                </Text>
              ) : null}
            </>
          ) : null}

          {shouldShowAddressField ? (
            <>
              <Divider my="md" />
              <Text fw={600} size="sm">
                Street address
              </Text>

              <TextInput
                mt={6}
                placeholder="Start typing your address…"
                value={address}
                onChange={(e) => {
                  onChangeAddress(e.currentTarget.value);
                  resetValidation();
                }}
                ref={addressInputRef}
                rightSection={
                  !placesReady && !placesErr ? (
                    <Text size="xs" c="dimmed">
                      Loading…
                    </Text>
                  ) : null
                }
              />

              {placesErr ? (
                <Text size="xs" c="red" mt={6}>
                  {placesErr}
                </Text>
              ) : !placesReady ? (
                <Text size="xs" c="dimmed" mt={6}>
                  Loading address suggestions…
                </Text>
              ) : (
                <Text size="xs" c="dimmed" mt={6}>
                  Select a suggested address to fill this field.
                </Text>
              )}
            </>
          ) : null}
        </Paper>

        <Space h="md" />

        <Button variant="subtle" onClick={onBack}>
          Back
        </Button>

        {/* Bottom bar */}
        <Box
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.92)",
            borderTop: "1px solid rgba(0,0,0,0.08)",
            padding: 16,
            backdropFilter: "blur(10px)",
          }}
        >
          <Container size={700} px="md">
            <Group gap="xs" grow>
              <Button
                fullWidth
                size="md"
                disabled={bottomButtonDisabled}
                onClick={onPressBottom}
              >
                {bottomButtonTitle}
              </Button>
            </Group>

            {!hasExistingPass && regoHasExistingPass !== false ? (
              <Text size="xs" c="dimmed" mt={8}>
                Enter your rego to check for an existing pass.
              </Text>
            ) : null}

            {!hasExistingPass && regoHasExistingPass === false && regoIsValid !== true ? (
              <Text size="xs" c="dimmed" mt={8}>
                Add your address, then validate your registration number before continuing.
              </Text>
            ) : null}
          </Container>
        </Box>
      </Container>
    </Container>
  );
}
